<template>
  <div class="calendar">
      <div class="calendar-header">
        <div class="lft">
            <div class="angl">
                <span @click="prevMonth" v-if="!isMonthYearPickerMode">
                    <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none">
                        <path d="M15 6L9 12L15 18" stroke="inherit" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </span>
                <span @click="nextMonth" v-if="!isMonthYearPickerMode">
                    <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none">
                        <path d="M9 19L16 12L9 5" stroke="inherit" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </span>
            </div>
            <span 
            class="today" 
            @click="goToToday"
            :class="{ 'not-on-today': !isCurrentMonth }"
            >today</span>
        </div>
        <span @click="toggleMonthYearPickerMode">
            <span v-if="isMonthYearPickerMode">{{ currentMonthName }} {{ currentYear }}</span>
            <span v-else>{{ months[currentMonth] }} {{ currentYear }}</span>
        </span>
        <div class="rngepke">
            <span v-if="isMobileView">Week View</span>
            <span v-else>Month View</span>
        </div>
      </div>

      <div v-if="isMonthYearPickerMode" class="month-year-picker">
          <select v-model="selectedMonth" @change="updateMonth">
              <option v-for="(month, index) in months" :key="index" :value="index">{{ month }}</option>
          </select>
          <select v-model="selectedYear" @change="updateYear">
              <option v-for="year in yearRange" :key="year" :value="year">{{ year }}</option>
          </select>
      </div>

      <div class="calendar-wrap">
        <!-- <div class="calendar-days">
            <span v-for="day in daysOfWeek" :key="day">{{ day }}</span>
        </div> -->
        
            <template v-if="isMobileView">
                <div class="calendar-days">
                    <span
                        v-for="(day, index) in currentWeekDates"
                        :key="index"
                        :class="['calendar-date', { selected: isSelected(day), today: isToday(day), hasEvent: getEventsForDate(day).length, 'not-current': !isCurrentMonth }]"
                        @click="selectDate(day)"
                    >
                        <div class="date-number">{{ day.getDate() }}</div>
                        <span v-if="getEventsForDate(day).length" class="event-dot"></span>
                    </span>
                </div>
                <div class="event-details-mobile" v-if="isMobileView">
                    <h4>{{ selectedDate ? selectedDate.toDateString() : 'Select a day' }}</h4>
                    <div v-for="event in getEventsForDate(selectedDate)" 
                        :key="event.id" 
                        class="event-pill full" 
                        :style="{ backgroundColor: event.color || '#1976d2' }" 
                        @click="openEvent(event)"
                    >
                        <div>
                            <span v-if="event.startTime"><strong>{{ formatTime12h(event.startTime) }}</strong></span>
                            <span v-if="event.endTime"> - <strong>{{ formatTime12h(event.endTime) }}</strong></span>
                            {{ event.title }}
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="calendar-days">
                    <span v-for="day in daysOfWeek" :key="day">{{ day }}</span>
                </div>
                <div class="calendar-dates">
                    <template v-for="(item, index) in daysInMonth" :key="index">
                        <span
                        :class="[
                            'calendar-date',
                            { selected: isSelected(item.date), today: isToday(item.date), 'not-current': !item.isCurrentMonth }
                        ]"
                        @click="selectDate(item.date)"
                        >
                            <div class="date-number">{{ item.date.getDate() }}</div>

                            <div class="event-list">
                                <div
                                    v-for="(event, idx) in visibleEventsByDate[item.date.toISOString().split('T')[0]] || []"
                                    :key="idx"
                                    class="event-pill"
                                    :style="{ backgroundColor: event.color || '#1976d2' }"
                                    @click.stop="openEvent(event)"
                                >
                                    <span>
                                        <strong v-if="event._showTime && event.startTime">
                                        {{ formatTime12h(event.startTime) }} - 
                                        </strong>
                                        {{ event.title }}
                                        <em v-if="event._continuing && event.continuous" style="font-size: 0.7em; font-style: italic; margin-left: 4px;">(ongoing)</em>
                                    </span>
                                </div>

                                <div
                                    v-if="getEventsForDate(item.date).length > 4"
                                    class="more-events"
                                    @click.stop="showMoreEvents(item.date)"
                                >
                                +{{ getEventsForDate(item.date).length - 4 }} more
                                </div>
                            </div>
                        </span>
                    </template>
                </div>
            </template>
      </div>
  </div>
  <div v-if="showModal" class="event-modal-overlay" @click.self="closeModal">
    <div class="event-modal">
        <h4>Events for {{ selectedEventDate ? selectedEventDate.toDateString() : '' }}</h4>
        <div
            v-for="event in getEventsForDate(selectedEventDate)"
            :key="event.id"
            class="event-pill full"
            :style="{ backgroundColor: event.color || '#1976d2' }"
            @click="openEvent(event)"
        >
            <div>
                <span v-if="event.startTime">
                <strong>{{ formatTime12h(event.startTime) }}</strong>
                <span v-if="event.endTime"> - <strong>{{ formatTime12h(event.endTime) }}</strong></span>
                &nbsp;
                </span>
                {{ event.title }}
            </div>
        </div>
        <button @click="closeModal">Close</button>
    </div>
  </div>
</template>
<script>
import { ref, computed, onMounted } from "vue";

export default {
    name: "CalendarView",
    props: {
        modelValue: [String, Date],
        events: {
            type: Array,
            default: () => []
        }
    },
    emits: ['update:modelValue', 'event-click'],
    setup(props, { emit }) {
        const showModal = ref(false);
        const selectedEventDate = ref(null);
        const renderedSpanningEvents = ref(new Set());
        const selectedDate = ref(props.modelValue ? new Date(props.modelValue) : new Date());

        const shouldRenderSpanningEvent = (event, date) => {
        if (!date) return false;

        const id = event.id;
        const start = new Date(event.start || event.date);
        const isStartDay = isSameDay(start, date);

        // If it's a spanning event, only render on start day
        if (event.spanning) {
            if (!isStartDay || renderedSpanningEvents.value.has(id)) {
            return false;
            }
            renderedSpanningEvents.value.add(id);
            return true;
        }

        return true; // Render normal events
        };
        const showMoreEvents = (date) => {
            selectedEventDate.value = date;
            showModal.value = true;
            console.log("date ",date)
        };

        const closeModal = () => {
        showModal.value = false;
        };

        const openEvent = (event) => {
            emit("event-click", event);
        };
        const today = new Date();
        const currentYear = ref(today.getFullYear());
        const currentMonth = ref(today.getMonth());

        const isMonthYearPickerMode = ref(false);
        const selectedMonth = ref(currentMonth.value);
        const selectedYear = ref(currentYear.value);

        const isMobileView = ref(window.innerWidth < 768);

        onMounted(() => {
            window.addEventListener('resize', () => {
                isMobileView.value = window.innerWidth < 768;
            });
        });

        const currentWeekDates = computed(() => {
            const selected = selectedDate.value || today;
            const startOfWeek = new Date(selected);
            startOfWeek.setDate(selected.getDate() - selected.getDay());
            return Array.from({ length: 7 }, (_, i) => {
                const d = new Date(startOfWeek);
                d.setDate(d.getDate() + i);
                return d;
            });
        });

        const formattedDate = computed(() =>
            selectedDate.value ? selectedDate.value.toLocaleDateString('en-CA') : ""
        );

        const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

        const yearRange = computed(() => {
            let range = [];
            for (let i = 1900; i <= today.getFullYear() + 10; i++) {
                range.push(i);
            }
            return range;
        });

        const firstDayOfMonth = computed(() => {
            return new Date(currentYear.value, currentMonth.value, 1).getDay();
        });

        const daysInMonth = computed(() => {
            const days = [];

            const firstOfMonth = new Date(currentYear.value, currentMonth.value, 1);
            const lastOfMonth = new Date(currentYear.value, currentMonth.value + 1, 0);

            const daysInCurrentMonth = lastOfMonth.getDate();
            const startDayOfWeek = firstOfMonth.getDay();

            // Get days from previous month to fill the gap at the start
            const prevMonthLastDate = new Date(currentYear.value, currentMonth.value, 0).getDate();
            for (let i = startDayOfWeek - 1; i >= 0; i--) {
                const date = new Date(currentYear.value, currentMonth.value - 1, prevMonthLastDate - i);
                days.push({
                    date,
                    isCurrentMonth: false
                });
            }

            // Current month days
            for (let i = 1; i <= daysInCurrentMonth; i++) {
                const date = new Date(currentYear.value, currentMonth.value, i);
                days.push({
                    date,
                    isCurrentMonth: true
                });
            }

            // Fill in the rest with next month's days
            const totalCells = 42;
            const nextDaysToAdd = totalCells - days.length;
            for (let i = 1; i <= nextDaysToAdd; i++) {
                const date = new Date(currentYear.value, currentMonth.value + 1, i);
                days.push({
                    date,
                    isCurrentMonth: false
                });
            }

            return days;
        });

        // const getEventsForDate = (date) => {
        //     if (!(date instanceof Date)) return [];
        //     const dateStr = date.toISOString().split('T')[0];
        //     return props.events.filter(event => event.date === dateStr);
        // };

        const isToday = (date) => {
            const today = new Date();
            return date && date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
        };

        const isSelected = (date) => {
            return selectedDate.value && date && date.getDate() === selectedDate.value.getDate() && date.getMonth() === selectedDate.value.getMonth() && date.getFullYear() === selectedDate.value.getFullYear();
        };

        const selectDate = (date) => {
            selectedDate.value = date;
            console.log("selected");
            // emit("update:modelValue", formattedDate.value);
        };

        const prevMonth = () => {
            if(isMobileView.value){
                selectedDate.value = new Date(
                    selectedDate.value.getFullYear(),
                    selectedDate.value.getMonth(),
                    selectedDate.value.getDate() - 7
                );
                currentMonth.value = selectedDate.value.getMonth();
                currentYear.value = selectedDate.value.getFullYear();
            }else{
                if (currentMonth.value === 0) {
                    currentMonth.value = 11;
                    currentYear.value--;
                } else {
                    currentMonth.value--;
                }
            }
        };

        const nextMonth = () => {
            if(isMobileView.value){
                selectedDate.value = new Date(
                    selectedDate.value.getFullYear(),
                    selectedDate.value.getMonth(),
                    selectedDate.value.getDate() + 7
                );
                currentMonth.value = selectedDate.value.getMonth();
                currentYear.value = selectedDate.value.getFullYear();
            }else{
                if (currentMonth.value === 11) {
                    currentMonth.value = 0;
                    currentYear.value++;
                } else {
                    currentMonth.value++;
                }
            }
        };

        const currentMonthName = computed(() => months[currentMonth.value]);

        const toggleMonthYearPickerMode = () => {
            isMonthYearPickerMode.value = !isMonthYearPickerMode.value;
        };

        const updateMonth = () => {
            currentMonth.value = selectedMonth.value;
            isMonthYearPickerMode.value = false;
        };

        const updateYear = () => {
            currentYear.value = selectedYear.value;
            isMonthYearPickerMode.value = false;
        };

        const isSameDay = (d1, d2) =>
            d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();

        const isEventOnDate = (event, date) => {
            if (!date) return false;

            const eventStart = event.start || event.date;
            const eventEnd = event.end || event.date;

            if (!eventStart) return false;

            const start = new Date(eventStart);
            const end = new Date(eventEnd);
            const checkDate = new Date(date);

            // Normalize all to midnight for comparison
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);

            return checkDate >= start && checkDate <= end;
        };

        const getEventsForDate = (date) => {
            if (!(date instanceof Date)) return [];

            const filtered = props.events.filter((event) => isEventOnDate(event, date));

            return filtered.sort((a, b) => {
                const timeA = a.startTime || '23:59'; // fallback so untimed events go last
                const timeB = b.startTime || '23:59';
                return timeA.localeCompare(timeB);
            });
        };

        const goToToday = () => {
            const t = new Date();
            currentYear.value = t.getFullYear();
            currentMonth.value = t.getMonth();
            selectedDate.value = t;
        };

        const isCurrentMonth = computed(() => {
            const todayDate = new Date();
            return (
                currentMonth.value === todayDate.getMonth() &&
                currentYear.value === todayDate.getFullYear()
            );
        });

        const formatTime12h = (timeStr) => {
            if (!timeStr) return '';

            const [hour, minute] = timeStr.split(':').map(Number);
            const suffix = hour >= 12 ? 'p' : 'a';
            const formattedHour = hour % 12 || 12;

            return `${formattedHour}:${minute.toString().padStart(2, '0')}${suffix}`;
        };

        const getEventStyle = (event) => {
            const bg = event.color || '#1976d2';

            if (event.spanning) {
                const start = new Date(event.start);
                const end = new Date(event.end);
                const daysSpanned = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

                return {
                backgroundColor: bg,
                gridColumn: `span ${daysSpanned}`
                };
            }

            return { backgroundColor: bg };
        };

        const visibleEventsByDate = computed(() => {
            const map = {};

            props.events.forEach((event) => {

                const startDate = new Date(event.start || event.date);
                const endDate = new Date(event.end || event.date);

                const start = new Date(startDate.setHours(0, 0, 0, 0));
                const end = new Date(endDate.setHours(0, 0, 0, 0));

                const span = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

                for (let i = 0; i < span; i++) {
                const currentDate = new Date(start);
                currentDate.setDate(start.getDate() + i);
                const key = currentDate.toISOString().split("T")[0];

                if (!map[key]) map[key] = [];

                const isStart = i === 0;
                const showTime = !event.continuous || isStart;

                map[key].push({
                    ...event,
                    _continuing: !isStart,
                    _showTime: showTime,
                    _isStart: isStart,
                });
                }
            });

            // Sort each day's events
            Object.keys(map).forEach((key) => {
                map[key] = map[key]
                .sort((a, b) => {
                    // If both are continuing, keep order
                    if (a._continuing && b._continuing) return 0;

                    // Put continuing (not starting) events at the top
                    if (a._continuing && !b._continuing) return -1;
                    if (!a._continuing && b._continuing) return 1;

                    // On start day or non-continuous — sort by time
                    const timeA = a.startTime || '23:59';
                    const timeB = b.startTime || '23:59';
                    return timeA.localeCompare(timeB);
                })
                .slice(0, 4); // Keep only first 4 for pills
            });

            return map;
        });
        

        return {
            goToToday,isCurrentMonth,selectedDate,
            currentYear,getEventStyle,
            currentMonth,currentWeekDates,
            daysOfWeek,visibleEventsByDate,
            months,
            daysInMonth,
            isToday,
            isSelected,isMobileView,
            selectDate,
            prevMonth,
            nextMonth,
            formattedDate,
            currentMonthName,
            toggleMonthYearPickerMode,
            yearRange,
            selectedMonth,
            selectedYear,formatTime12h,
            updateMonth,
            updateYear,showModal,isSameDay,
            isMonthYearPickerMode,selectedEventDate,shouldRenderSpanningEvent,
            firstDayOfMonth,showMoreEvents,closeModal,openEvent,getEventsForDate
        };
    }
};
</script>


<style scoped>
    .selected{border: 2px solid var(--primary-light) !important;}
    .calendar-view { position: relative; }
    .calendar-view input { margin-bottom: 0 !important; }

    .calendar {
        top: 100%;
        left: 0;
        background-color: var(--white-color);
        border: 1px solid var(--data-table-border);
        border-radius: 5px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        width: 100%;
        padding: 10px;
        color: var(--black);
    }

    .calendar-header {
        display: flex; align-items: center;
        justify-content: space-between;
        margin-bottom: 10px;
    }
    .calendar .lft{display:flex; align-items: center; justify-content: center;
        gap: 10px;}
    .calendar-header .angl{display:flex; border-radius:5px; overflow:hidden}
    .angl span:first-child{border-right: 1px solid var(--white-color);}
    .angl span{background: var(--light-row-color);
    width: 45px; height: 35px; display: flex;
    align-items: center; justify-content: center; cursor: pointer;}
    .angl span svg{stroke: var(--black);}
    .lft .today{background: var(--data-table-border); height: 35px;
    display: flex; align-items: center; padding: 0 10px; border-radius: 5px;
    cursor: pointer; user-select: none;}
    .calendar-wrap{border: 1px solid var(--data-table-border);}
    .calendar .rngepke{display:flex;border-radius:5px; overflow:hidden}
    .rngepke span{background: var(--light-row-color); padding: 0 10px; height: 35px;
    display: flex; cursor: pointer; align-items: center; justify-content: center;
    user-select: none;}
    .calendar-days span{text-align: center; border-right: 1px solid var(--data-table-border);
    padding: 10px;}
    
    .calendar-header > span{cursor:pointer;}

    .calendar-days, .calendar-dates {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        text-align: right;
    }

    .calendar-date {
        padding: 5px 0; cursor: pointer; border: 1px solid var(--data-table-border); border-right: 0;
        border-bottom: 0; min-height: 110px;
    }

    .calendar-date.today {
        font-weight: bold;
        color: var(--primary-color);
        border: 2px solid var(--primary-color);
    }

    .month-year-picker select {
        margin-right: 5px;
    }
    .calendar-date.not-current {color: var(--odd-td-first);
    }
    .event-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
}

.event-pill {
  padding: 2px 4px;
  font-size: 10px;
  color: white;
  border-radius: 3px;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
}

.more-events {
  font-size: 10px;
  color: var(--primary-color);
  cursor: pointer;
  margin-top: 2px;
}

.event-modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.event-modal {
  background: var(--white-color);
border-radius: 8px;
padding: 20px;
max-width: 300px;
width: 90%;
color: var(--black);
}

.event-pill.full {
  width: 100%;
  margin-bottom: 5px;
}
.event-pill.spanning {
  font-style: italic; border-radius: 0;
}
.date-number{padding-right: 5px;}
.not-on-today{background: var(--light-row-color) !important;}
.event-pill.spanning {
  position: absolute;
  top: 2px;
  left: 2px;
  height: 20px;
  font-size: 11px;
  white-space: nowrap;
  z-index: 2;
}
.event-dot {
    display: block;
    width: 5px;
    height: 5px;
    background-color: var(--primary-color);
    border-radius: 50%;
    padding: 0 !important;
}

@media (max-width: 768px) {
  .calendar-header {
    flex-direction: column;
    gap: 5px;
    align-items: center;
  }

  .calendar-days{border-bottom: 1px solid var(--data-table-border);}

  .calendar-days,
  .calendar-dates {
    grid-template-columns: repeat(7, 1fr);
    overflow-x: auto;
  }

  .calendar-date {
    min-height: 70px;
    position: relative;
    display: flex;
    align-items: center;
    flex-direction: column;
  }

  .event-list {
    display: none;
  }

  .calendar-date.has-event::after {
    content: '';
    width: 6px;
    height: 6px;
    background: var(--primary-color);
    border-radius: 50%;
    position: absolute;
    bottom: 6px;
    left: 50%;
    transform: translateX(-50%);
  }

  .event-details-mobile {
    margin-top: 1rem;
    background: var(--light-row-color);
    border-radius: 6px;
    padding: 10px;
  }
  .date-number{padding: 0 !important}
  .not-current .event-dot {background-color:var(--odd-td-first);}
}

</style>